import { useMemo } from "react";
import {
  calculateBounds,
  convertGPSToSVG,
  createPathFromPoints,
} from "@/utils/gpsProjection";
import {
  COMPARISON_COLOR,
  REFERENCE_COLOR,
  normalizeSelection,
} from "./comparisonMath";

interface ComparisonTrackMapProps {
  referenceTrack: Array<[number, number]>;
  comparisonTrack: Array<[number, number]>;
  cursorIndex: number | null;
  selection: [number, number] | null;
  referenceLabel: string;
  comparisonLabel: string;
  className?: string;
}

const WIDTH = 400;
const HEIGHT = 300;

function toPoints(track: Array<[number, number]>) {
  return track.map(([lat, lng]) => ({ lat, lng }));
}

/**
 * Both racing lines drawn from the aligned series (no track layout file
 * needed), the selected section highlighted, and one marker per lap at the
 * shared position cursor.
 */
export default function ComparisonTrackMap({
  referenceTrack,
  comparisonTrack,
  cursorIndex,
  selection,
  referenceLabel,
  comparisonLabel,
  className,
}: ComparisonTrackMapProps) {
  const { refPoints, cmpPoints } = useMemo(() => {
    const ref = toPoints(referenceTrack);
    const cmp = toPoints(comparisonTrack);
    const bounds = calculateBounds([...ref, ...cmp]);
    return {
      refPoints: convertGPSToSVG(ref, bounds, WIDTH, HEIGHT, 18),
      cmpPoints: convertGPSToSVG(cmp, bounds, WIDTH, HEIGHT, 18),
    };
  }, [referenceTrack, comparisonTrack]);

  const refPath = useMemo(() => createPathFromPoints(refPoints), [refPoints]);
  const cmpPath = useMemo(() => createPathFromPoints(cmpPoints), [cmpPoints]);
  const selectionPaths = useMemo(() => {
    if (!selection) return null;
    const [a, b] = normalizeSelection(selection[0], selection[1]);
    return {
      ref: createPathFromPoints(refPoints.slice(a, b + 1)),
      cmp: createPathFromPoints(cmpPoints.slice(a, b + 1)),
    };
  }, [selection, refPoints, cmpPoints]);

  const refCursor = cursorIndex !== null ? refPoints[cursorIndex] : undefined;
  const cmpCursor = cursorIndex !== null ? cmpPoints[cursorIndex] : undefined;
  const start = refPoints[0];

  return (
    <div className={className}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-auto rounded-lg border border-[#1a1a2e] bg-[#080810]"
        role="img"
        aria-label={`${referenceLabel} / ${comparisonLabel}`}
        data-testid="comparison-track-map"
      >
        <path d={refPath} fill="none" stroke={REFERENCE_COLOR} strokeWidth={2.2} strokeOpacity={0.9} strokeLinejoin="round" />
        <path d={cmpPath} fill="none" stroke={COMPARISON_COLOR} strokeWidth={2} strokeOpacity={0.85} strokeLinejoin="round" />
        {selectionPaths && (
          <g data-testid="comparison-map-selection">
            <path d={selectionPaths.ref} fill="none" stroke="#ffffff" strokeWidth={7} strokeOpacity={0.25} strokeLinecap="round" strokeLinejoin="round" />
            <path d={selectionPaths.ref} fill="none" stroke={REFERENCE_COLOR} strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
            <path d={selectionPaths.cmp} fill="none" stroke={COMPARISON_COLOR} strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
          </g>
        )}
        {start && (
          <rect x={start.x - 4} y={start.y - 4} width={8} height={8} fill="#e5e7eb" stroke="#080810" strokeWidth={1.5} transform={`rotate(45 ${start.x} ${start.y})`} />
        )}
        {cmpCursor && (
          <circle cx={cmpCursor.x} cy={cmpCursor.y} r={6} fill={COMPARISON_COLOR} stroke="#080810" strokeWidth={2} data-testid="comparison-marker" />
        )}
        {refCursor && (
          <circle cx={refCursor.x} cy={refCursor.y} r={6} fill={REFERENCE_COLOR} stroke="#080810" strokeWidth={2} data-testid="reference-marker" />
        )}
      </svg>
      <div className="mt-2 flex items-center gap-4 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: REFERENCE_COLOR }} />
          {referenceLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: COMPARISON_COLOR }} />
          {comparisonLabel}
        </span>
      </div>
    </div>
  );
}
